/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { ensureCurrentUser } from '../../../../util/data';
import { getCurrentUserTypeRoles } from '../../../../util/userHelpers';

import { AvatarLarge, ExternalLink, InlineTextButton, NamedLink } from '../../../../components';

import css from './TopbarMobileMenu.module.css';

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <li className={className}>
        <NamedLink name={name} params={params} to={to}>
          <span className={css.menuItemBorder} />
          {text}
        </NamedLink>
      </li>
    );
  }
  return (
    <li className={css.navigationLink}>
      <ExternalLink href={href}>
        <span className={css.menuItemBorder} />
        {text}
      </ExternalLink>
    </li>
  );
};

/**
 * Menu for mobile layout (opens through hamburger icon)
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string?} props.currentPage
 * @param {boolean} props.currentUserHasListings
 * @param {Object?} props.currentUser API entity
 * @param {number} props.bookingsNotificationCount
 * @param {number} props.messagesNotificationCount
 * @param {Array<Object>} props.customLinks Contains object like { group, text, type, href, route }
 * @param {Function} props.onLogout
 * @returns {JSX.Element} search icon
 */
const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    inboxTab,
    currentUser,
    config,
    bookingsNotificationCount = 0,
    messagesNotificationCount = 0,
    customLinks,
    onLogout,
    showCreateListingsLink,
    editListingParams,
  } = props;

  const user = ensureCurrentUser(currentUser);
  const roles = currentUser && config ? getCurrentUserTypeRoles(config, currentUser) : {};
  const showProfileSettingsLink = !(currentUser && roles?.provider);

  const extraLinks = customLinks.map((linkConfig, index) => {
    return (
      <CustomLinkComponent
        key={`${linkConfig.text}_${index}`}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  const createListingsLinkMaybe = showCreateListingsLink ? (
    <NamedLink className={css.createNewListingLink} name="NewListingPage">
      <FormattedMessage id="TopbarMobileMenu.newListingLink" />
    </NamedLink>
  ) : null;

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage
          id="TopbarMobileMenu.signupOrLogin"
          values={{ lineBreak: <br />, signup, login }}
        />
      </span>
    );

    const applyLink = (
      <NamedLink name="ApplyPage" className={css.applyLink}>
        <FormattedMessage id="TopbarMobileMenu.applyAsOwner" defaultMessage="Apply as an owner" />
      </NamedLink>
    );

    return (
      <nav className={css.root}>
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>

          <ul className={css.customLinksWrapper}>{extraLinks}</ul>

          <div className={css.spacer} />
        </div>
        <div className={css.footer}>
          {applyLink}
          {createListingsLinkMaybe}
        </div>
      </nav>
    );
  }

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    const isProfileSection =
      page === 'ProfileSettingsPage' &&
      (currentPage === 'ProfileSettingsPage' ||
        (currentUser && roles?.provider && currentPage === 'EditListingPage'));
    return currentPage === page || isAccountSettingsPage || isInboxPage || isProfileSection
      ? css.currentPage
      : null;
  };

  const manageListingsLinkMaybe = showCreateListingsLink ? (
    <li className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}>
      <NamedLink name="ManageListingsPage">
        <span className={css.menuItemBorder} />
        <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
      </NamedLink>
    </li>
  ) : null;

  const bookingsNotificationDot =
    bookingsNotificationCount > 0 ? <div className={css.notificationDot} /> : null;
  const messagesNotificationDot =
    messagesNotificationCount > 0 ? <div className={css.notificationDot} /> : null;

  return (
    <div className={css.root}>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>

        <ul className={css.accountLinksWrapper}>
          {manageListingsLinkMaybe}
          {roles?.provider ? (
            <>
              <li className={classNames(css.navigationLink, currentPageClass('DashboardPage'))}>
                <NamedLink name="DashboardPage">
                  <span className={css.menuItemBorder} />
                  <FormattedMessage id="TopbarMobileMenu.dashboardLink" />
                </NamedLink>
              </li>
              <li
                className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
              >
                {showProfileSettingsLink ? (
                  <NamedLink name="ProfileSettingsPage">
                    <span className={css.menuItemBorder} />
                    <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
                  </NamedLink>
                ) : editListingParams ? (
                  <NamedLink name="EditListingPage" params={editListingParams}>
                    <span className={css.menuItemBorder} />
                    <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
                  </NamedLink>
                ) : (
                  <NamedLink name="DashboardPage">
                    <span className={css.menuItemBorder} />
                    <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
                  </NamedLink>
                )}
              </li>
              <li className={classNames(css.navigationLink, currentPageClass('BookingsPage'))}>
                <NamedLink name="BookingsPage" params={{ tab: inboxTab }}>
                  <span className={css.menuItemBorder} />
                  <span className={css.menuLinkLabel}>
                    <FormattedMessage id="TopbarMobileMenu.bookingsLink" />
                    {bookingsNotificationDot}
                  </span>
                </NamedLink>
              </li>
              <li className={classNames(css.navigationLink, currentPageClass('PaymentsPage'))}>
                <NamedLink name="PaymentsPage" params={{ tab: inboxTab }}>
                  <span className={css.menuItemBorder} />
                  <FormattedMessage id="TopbarMobileMenu.earningsLink" />
                </NamedLink>
              </li>
              <li
                className={classNames(css.navigationLink, currentPageClass(`InboxPage:${inboxTab}`))}
              >
                <NamedLink name="InboxPage" params={{ tab: inboxTab }}>
                  <span className={css.menuItemBorder} />
                  <span className={css.menuLinkLabel}>
                    <FormattedMessage id="TopbarMobileMenu.messagesLink" />
                    {messagesNotificationDot}
                  </span>
                </NamedLink>
              </li>
            </>
          ) : (
            <>
              <li className={classNames(css.navigationLink, currentPageClass('DashboardPage'))}>
                <NamedLink name="DashboardPage">
                  <span className={css.menuItemBorder} />
                  <FormattedMessage id="TopbarMobileMenu.overviewLink" />
                </NamedLink>
              </li>
              <li className={classNames(css.navigationLink, currentPageClass('BookingsPage'))}>
                <NamedLink name="BookingsPage" params={{ tab: inboxTab }}>
                  <span className={css.menuItemBorder} />
                  <span className={css.menuLinkLabel}>
                    <FormattedMessage id="TopbarMobileMenu.bookingsLink" />
                    {bookingsNotificationDot}
                  </span>
                </NamedLink>
              </li>
              <li className={classNames(css.navigationLink, currentPageClass('PaymentsPage'))}>
                <NamedLink name="PaymentsPage" params={{ tab: inboxTab }}>
                  <span className={css.menuItemBorder} />
                  <FormattedMessage id="TopbarMobileMenu.paymentsLink" />
                </NamedLink>
              </li>
              <li
                className={classNames(css.navigationLink, currentPageClass(`InboxPage:${inboxTab}`))}
              >
                <NamedLink name="InboxPage" params={{ tab: inboxTab }}>
                  <span className={css.menuItemBorder} />
                  <span className={css.menuLinkLabel}>
                    <FormattedMessage id="TopbarMobileMenu.messagesLink" />
                    {messagesNotificationDot}
                  </span>
                </NamedLink>
              </li>
              <li
                className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
              >
                <NamedLink name="ProfileSettingsPage">
                  <span className={css.menuItemBorder} />
                  <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
                </NamedLink>
              </li>
            </>
          )}
          <li className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}>
            <NamedLink name="AccountSettingsPage">
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
            </NamedLink>
          </li>
        </ul>
        <ul className={css.customLinksWrapper}>{extraLinks}</ul>
        <div className={css.spacer} />
      </div>
      <div className={css.footer}>
        <NamedLink name="ApplyPage" className={css.applyLink}>
          <FormattedMessage id="TopbarMobileMenu.applyAsOwner" />
        </NamedLink>
        {createListingsLinkMaybe}
      </div>
    </div>
  );
};

export default TopbarMobileMenu;
